<script>

import bem from '@/utils/bem';
import { mapActions, mapState } from 'vuex';
import ModalWrapper from '@/components/modals/Modal-wrapper.vue';
import { ColorPicker } from 'vue3-colorpicker';
import {
  DeleteOutlined, EditOutlined, PlusOutlined,
} from '@ant-design/icons-vue';

const b = bem('frames-color');

export default {
  components: {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ColorPicker,
    ModalWrapper,
  },
  props: {
    // Optional — when omitted, component shows a material selector
    selectedMaterialId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      localMaterialId: this.selectedMaterialId,
      selectedColor: null,
      newName: '',
      newColor: '',
      isShowPopup: false,
    };
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getFrameColorsAction: 'createTemplate/getFrameColors',
      editFrameColorAction: 'createTemplate/editFrameColor',
      createFrameColorAction: 'createTemplate/createFrameColor',
      deleteFrameColorAction: 'createTemplate/deleteFrameColor',
    }),
    change(color) {
      this.newName = color?.name || '';
      this.newColor = color?.colorHex || '';
      this.selectedColor = color || null;
      this.isShowPopup = true;
    },
    save() {
      if (
        this.selectedColor
        && Object.keys(this.selectedColor).length
      ) {
        this.editFrameColorAction({
          id: this.selectedColor.id,
          body: {
            name: this.newName,
            colorHex: this.newColor,
          },
        });
      } else {
        this.createFrameColorAction({
          body: {
            materialId: this.activeMaterialId,
            name: this.newName,
            colorHex: this.newColor,
          },
        });
      }
    },
    cancel() {
      this.newName = '';
      this.newColor = '';
      this.selectedColor = null;
      this.isShowPopup = false;
    },
  },
  computed: {
    ...mapState({
      colors: (state) => state.createTemplate.colors,
      materials: (state) => state.createTemplate.materials,
    }),
    // Use prop if provided, otherwise local selection
    activeMaterialId() {
      return this.selectedMaterialId || this.localMaterialId;
    },
    materialOptions() {
      return (this.materials || []).map((m) => ({
        label: m.name,
        value: m.id,
      }));
    },
  },
  mounted() {
    this.getFrameColorsAction();
    // Auto-select first material if none provided via prop
    if (!this.activeMaterialId && this.materials?.length) {
      this.localMaterialId = this.materials[0].id;
    }
  },
  watch: {
    // When materials load after mount, auto-select first
    materials(val) {
      if (!this.activeMaterialId && val?.length) {
        this.localMaterialId = val[0].id;
      }
    },
  },
};
</script>

<template>
  <div :class="b()">
    <div :class="b('header')">
      <h3 :class="b('title')">Frame Colors</h3>
      <a-button
        :class="b('add-btn')"
        type="primary"
        :disabled="!activeMaterialId"
        @click="change()"
      >
        <template #icon><plus-outlined /></template>
        Add color
      </a-button>
    </div>

    <!-- Material selector — shown when no selectedMaterialId prop -->
    <div v-if="!selectedMaterialId" style="margin-bottom: 16px;">
      <a-select
        v-model:value="localMaterialId"
        :options="materialOptions"
        placeholder="Select collection"
        style="width: 300px;"
      />
    </div>

    <div :class="b('grid')">
      <div
        v-for="item in colors[activeMaterialId]"
        :key="item.id"
        :class="b('card', { error: !item.colorHex })"
      >
        <div :class="b('card-preview')">
          <div
            v-if="item.colorHex"
            :class="b('card-color')"
            :style="{ backgroundColor: item.colorHex }"
          />
          <div v-else :class="b('card-error')">
            <span>No color</span>
          </div>
        </div>
        <div :class="b('card-content')">
          <span :class="b('card-name')">{{ item.name }}</span>
          <span :class="b('card-hex')">{{ item.colorHex || 'Not set' }}</span>
        </div>
        <div :class="b('card-actions')">
          <a-button
            :class="b('action-btn')"
            type="text"
            size="small"
            @click="change(item)"
          >
            <template #icon><edit-outlined /></template>
          </a-button>
          <a-popconfirm
            title="Are you sure you want to delete this color?"
            ok-text="Yes"
            cancel-text="No"
            @confirm="deleteFrameColorAction(item.id)"
          >
            <a-button
              :class="b('action-btn')"
              type="text"
              danger
              size="small"
            >
              <template #icon><delete-outlined /></template>
            </a-button>
          </a-popconfirm>
        </div>
      </div>
    </div>

    <modal-wrapper v-model:show="isShowPopup" width="420px">
      <div :class="b('modal')">
        <h3 :class="b('modal-title')">
          {{ selectedColor ? 'Edit Color' : 'Add New Color' }}
        </h3>

        <div :class="b('modal-field')">
          <span :class="b('modal-label')">Color Name</span>
          <a-input
            v-model:value="newName"
            placeholder="Enter color name"
            size="large"
          />
        </div>

        <div :class="b('modal-field')">
          <span :class="b('modal-label')">Select Color</span>
          <div :class="b('modal-picker-wrap')">
            <ColorPicker
              v-model:pureColor="newColor"
              format="hex6"
              :isWidget="true"
              :disableAlpha="true"
            />
          </div>
        </div>

        <div v-if="newColor" :class="b('modal-preview')">
          <div
            :class="b('modal-preview-color')"
            :style="{ backgroundColor: newColor }"
          />
          <span :class="b('modal-preview-hex')">{{ newColor }}</span>
        </div>

        <div :class="b('modal-actions')">
          <a-button
            type="primary"
            size="large"
            :class="b('modal-save-btn')"
            @click="save()"
          >
            {{ selectedColor ? 'Save Changes' : 'Add Color' }}
          </a-button>
          <a-button
            size="large"
            @click="cancel()"
          >
            Cancel
          </a-button>
        </div>
      </div>
    </modal-wrapper>
  </div>
</template>

<style src="./style.scss" lang="scss" scoped />
